import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Home, MapPin, Users, DollarSign, Building, Search, ArrowRight, Star } from "lucide-react";
import { Helmet } from "react-helmet";
import axios from "axios";
import { dfwCounties, dfwCities } from "@/data/dfwGeoData";
import SiteNavigation from "@/components/SiteNavigation";

const API = process.env.REACT_APP_BACKEND_URL;

const CountyLanding = ({ user }) => {
  const { countySlug } = useParams();
  const navigate = useNavigate();
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);

  // Find county by slug (e.g., "dallas-county" -> "dallas")
  const countyKey = countySlug?.replace('-county', '');
  const county = dfwCounties[countyKey];

  // All hooks MUST be called before any conditional returns
  useEffect(() => {
    if (county) {
      fetchListings();
    }
  }, [countySlug, county]);

  const fetchListings = async () => {
    if (!county) return;
    try {
      setLoading(true);
      const response = await axios.get(`${API}/api/listings`, {
        params: { county: county.name, limit: 12, status: "active" }
      });
      setListings(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error("Error fetching listings:", error);
      setListings([]);
    } finally {
      setLoading(false);
    }
  };

  // If county not found, show 404-like message (AFTER all hooks)
  if (!county) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 to-amber-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">County Not Found</h1>
          <p className="text-gray-600 mb-6">We couldn&apos;t find FSBO listings for this county.</p>
          <Button onClick={() => navigate('/search')}>Browse All Listings</Button>
        </div>
      </div>
    );
  }

  // Get cities with full data for this county
  const countyCities = county.cities.map(cityName => {
    const cityEntry = Object.entries(dfwCities).find(([slug, data]) => data.name === cityName);
    return cityEntry ? { slug: cityEntry[0], ...cityEntry[1] } : { name: cityName, slug: cityName.toLowerCase().replace(' ', '-') };
  }).filter(c => c);

  // Schema.org structured data
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": `${county.name} County Homes For Sale By Owner | FSBO ${county.name} County TX`,
    "description": `Find ${county.name} County, Texas homes for sale by owner. Browse FSBO listings in all ${county.cities.length}+ cities. Save thousands on agent fees!`,
    "url": `https://its-sold.com/county/${countySlug}`,
    "mainEntity": {
      "@type": "Place",
      "name": `${county.name} County, Texas`,
      "description": county.description,
      "geo": {
        "@type": "GeoCoordinates",
        "addressRegion": "TX",
        "addressCountry": "US"
      }
    },
    "breadcrumb": {
      "@type": "BreadcrumbList",
      "itemListElement": [
        { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://its-sold.com" },
        { "@type": "ListItem", "position": 2, "name": "Texas", "item": "https://its-sold.com/homes-for-sale" },
        { "@type": "ListItem", "position": 3, "name": `${county.name} County`, "item": `https://its-sold.com/county/${countySlug}` }
      ]
    }
  };

  const localBusinessData = {
    "@context": "https://schema.org",
    "@type": "RealEstateAgent",
    "name": `Its-Sold.com - ${county.name} County FSBO`,
    "description": `Free For Sale By Owner real estate listings in ${county.name} County, Texas. List your home for free and save thousands.`,
    "url": `https://its-sold.com/county/${countySlug}`,
    "areaServed": {
      "@type": "AdministrativeArea",
      "name": `${county.name} County`,
      "containedInPlace": {
        "@type": "State",
        "name": "Texas"
      }
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 to-amber-50">
      {/* SEO Meta Tags */}
      <Helmet>
        <title>{county.name} County Homes For Sale By Owner | FSBO {county.name} County TX | Its-Sold.com™</title>
        <meta name="description" content={`Find ${county.name} County, Texas homes for sale by owner (FSBO). Browse real estate listings in ${county.cities.slice(0, 5).join(', ')}, and more. FREE listings - save thousands!`} />
        <meta name="keywords" content={county.keywords} />
        <link rel="canonical" href={`https://its-sold.com/county/${countySlug}`} />
        
        {/* Open Graph */}
        <meta property="og:title" content={`${county.name} County FSBO Homes | For Sale By Owner | Its-Sold.com`} />
        <meta property="og:description" content={`Browse homes for sale by owner in ${county.name} County, TX. No agent fees!`} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`https://its-sold.com/county/${countySlug}`} />
        <meta property="og:image" content="https://i.imgur.com/OaQdm6f.jpg" />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`${county.name} County Homes For Sale By Owner`} />
        <meta name="twitter:description" content={`FSBO listings in ${county.name} County, TX. Save thousands - list FREE!`} />
        
        {/* Geo Tags */}
        <meta name="geo.region" content="US-TX" />
        <meta name="geo.placename" content={`${county.name} County, Texas`} />
        
        {/* Schema.org */}
        <script type="application/ld+json">{JSON.stringify(structuredData)}</script>
        <script type="application/ld+json">{JSON.stringify(localBusinessData)}</script>
      </Helmet>

      {/* Consistent Navigation */}
      <SiteNavigation />

      {/* Breadcrumb */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <nav className="text-sm text-gray-600">
            <ol className="flex items-center space-x-2">
              <li><Link to="/" className="hover:text-orange-600">Home</Link></li>
              <li>/</li>
              <li><Link to="/search" className="hover:text-orange-600">Texas</Link></li>
              <li>/</li>
              <li className="text-orange-600 font-medium">{county.name} County</li>
            </ol>
          </nav>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Hero Section */}
        <header className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            {county.name} County Homes For Sale By Owner
          </h1>
          <p className="text-xl text-gray-700 mb-2">
            FSBO Listings Across {county.cities.length}+ Cities in {county.name} County, Texas
          </p>
          <p className="text-base text-gray-600 max-w-3xl mx-auto mb-6">
            {county.description}
          </p>
          
          {/* Quick Stats */}
          <div className="flex flex-wrap justify-center gap-6 text-sm">
            <div className="flex items-center">
              <Users className="w-4 h-4 text-orange-600 mr-1" />
              <span><strong>Population:</strong> {county.population}</span>
            </div>
            <div className="flex items-center">
              <Building className="w-4 h-4 text-blue-600 mr-1" />
              <span><strong>County Seat:</strong> {county.seat}</span>
            </div>
            <div className="flex items-center">
              <MapPin className="w-4 h-4 text-green-600 mr-1" />
              <span><strong>Cities:</strong> {county.cities.length}+</span>
            </div>
          </div>
        </header>

        {/* Cities Grid */}
        <section className="mb-12">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6">
            Browse FSBO Homes by City in {county.name} County
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {countyCities.slice(0, 20).map((city) => (
              <Link
                key={city.slug || city.name}
                to={`/homes-for-sale/${city.slug}`}
                className="bg-white rounded-lg p-4 shadow hover:shadow-lg transition-shadow border border-gray-100 hover:border-orange-200"
              >
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="font-semibold text-gray-900">{city.name}</h3>
                    {city.medianPrice && (
                      <p className="text-sm text-gray-500">Median: {city.medianPrice}</p>
                    )}
                  </div>
                  <MapPin className="w-5 h-5 text-orange-500 flex-shrink-0" />
                </div>
                {city.population && (
                  <p className="text-xs text-gray-400 mt-2">Pop: {city.population}</p>
                )}
              </Link>
            ))}
          </div>
          
          {county.cities.length > 20 && (
            <p className="text-center text-gray-500 mt-4">
              + {county.cities.length - 20} more cities in {county.name} County
            </p>
          )}
        </section>

        {/* Benefits Section */}
        <section className="bg-gradient-to-r from-orange-600 to-amber-500 rounded-xl p-6 md:p-8 text-white mb-12">
          <h2 className="text-2xl md:text-3xl font-bold mb-6 text-center">
            Why Sell FSBO in {county.name} County?
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="text-4xl font-bold mb-2">$0</div>
              <p>To List Your Home</p>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold mb-2">6%</div>
              <p>Commission Saved</p>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold mb-2">{county.cities.length}+</div>
              <p>Cities Covered</p>
            </div>
          </div>
          <div className="text-center mt-6">
            <Button 
              onClick={() => navigate('/register')} 
              className="bg-white text-orange-600 hover:bg-gray-100 font-bold text-lg px-8 py-6"
            >
              List Your {county.name} County Home FREE →
            </Button>
          </div>
        </section>

        {/* Recent Listings */}
        <section className="mb-12">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900">
              Recent FSBO Listings in {county.name} County
            </h2>
            <Button onClick={() => navigate(`/search?county=${county.name}`)} variant="outline">
              View All <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>

          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-4 border-orange-600 border-t-transparent mx-auto"></div>
            </div>
          ) : listings.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {listings.slice(0, 6).map((listing) => (
                <Card
                  key={listing.id}
                  className="overflow-hidden hover:shadow-xl transition-shadow cursor-pointer group"
                  onClick={() => navigate(`/listing/${listing.id}`)}
                >
                  <div className="relative h-48 bg-gray-200">
                    {listing.photos?.[0] ? (
                      <img
                        src={listing.photos[0]}
                        alt={`${listing.title} - FSBO home in ${listing.city}, ${county.name} County, TX`}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        loading="lazy"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Home className="w-16 h-16 text-gray-400" />
                      </div>
                    )}
                    <div className="absolute top-3 left-3 bg-orange-600 text-white px-3 py-1 rounded-full text-sm font-bold">
                      FSBO
                    </div>
                  </div>
                  <div className="p-4">
                    <div className="text-2xl font-bold text-gray-900 mb-1">
                      ${listing.price?.toLocaleString()}
                    </div>
                    <p className="text-gray-600 text-sm mb-1">{listing.city}, TX</p>
                    <p className="text-gray-500 text-sm mb-2 line-clamp-1">{listing.address}</p>
                    <div className="flex items-center text-sm text-gray-500">
                      <span>{listing.bedrooms} bed</span>
                      <span className="mx-2">•</span>
                      <span>{listing.bathrooms} bath</span>
                      <span className="mx-2">•</span>
                      <span>{listing.sqft?.toLocaleString()} sqft</span>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          ) : (
            <Card className="p-8 text-center">
              <Home className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-700 mb-2">No Active Listings</h3>
              <p className="text-gray-500 mb-4">Be the first to list in {county.name} County!</p>
              <Button onClick={() => navigate('/register')} className="bg-orange-600 hover:bg-orange-700">
                List Your Home FREE
              </Button>
            </Card>
          )}
        </section>

        {/* Other Counties */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            Explore Other DFW Counties
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
            {Object.entries(dfwCounties)
              .filter(([key]) => key !== countyKey)
              .map(([key, countyInfo]) => (
                <Link
                  key={key}
                  to={`/county/${countyInfo.slug}`}
                  className="text-center p-3 bg-white rounded-lg shadow hover:shadow-md transition-shadow"
                >
                  <p className="font-medium text-gray-900">{countyInfo.name} County</p>
                  <p className="text-xs text-gray-500">{countyInfo.cities.length}+ cities</p>
                </Link>
              ))}
          </div>
        </section>

        {/* Final CTA */}
        <section className="bg-gray-900 text-white rounded-xl p-8 text-center">
          <h2 className="text-3xl font-bold mb-4">
            Sell Your {county.name} County Home Today
          </h2>
          <p className="text-gray-300 mb-6 max-w-2xl mx-auto">
            Join thousands of {county.name} County homeowners who have saved on commissions by selling FSBO.
          </p>
          <Button 
            onClick={() => navigate('/register')} 
            className="bg-orange-600 hover:bg-orange-700 text-white font-bold text-lg px-8 py-6"
          >
            List My Home FREE →
          </Button>
        </section>
      </div>
    </div>
  );
};

export default CountyLanding;
